import { useMemo, useState } from "react";
import EmptyState from "@/components/EmptyState";
import Table, { TableHeading, buildActionColumn } from "@/components/Table";
import type { Action } from "@/components/MenuButton";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { toFormat } from "@/lib/dateHelpers";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import { GET_MANAGEABLE_SECTIONS } from "@/pages/events/queries";
import {
  ACHIEVEMENTS_FOR_MEMBER_QUERY,
  NATIVE_ACHIEVEMENT_CODE,
  type MembershipAchievementForMember
} from "@/pages/people/[pgaId]/queries";
import DeleteAchievementModal from "@/pages/people/[pgaId]/_components/DeleteAchievementModal";
import EditAchievementModal from "@/pages/people/[pgaId]/_components/EditAchievementModal";

type AwardTableRow = {
  award: MembershipAchievementForMember;
  id: string;
  recognitionYear: string;
  recognizedBy: string;
  recognizedOn: string;
  title: string;
};

type AwardModalState = { mode: "add" } | { award: MembershipAchievementForMember; mode: "edit" };

const formatTableData = (achievements: MembershipAchievementForMember[]): AwardTableRow[] =>
  achievements.map((achievement, index) => ({
    id: `${achievement.id}-${index}`,
    award: achievement,
    title: achievement.title && achievement.title !== NATIVE_ACHIEVEMENT_CODE ? achievement.title : "--",
    recognitionYear: achievement.recognitionYear || "--",
    recognizedBy: achievement.recognizingEntity?.name || "--",
    recognizedOn: toFormat(achievement.recognizedAt || "") || "--"
  }));

export default function CareerAwards() {
  const { pgaId } = useParams();
  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const canManageAchievements = flags["backoffice-manage-member-achievements"] === true;
  const { data, loading, error } = useQuery(ACHIEVEMENTS_FOR_MEMBER_QUERY, {
    variables: { pgaId: pgaId!, achievement: "AWARD" },
    fetchPolicy: "cache-first",
    skip: !flagsLoaded
  });
  useQuery(GET_MANAGEABLE_SECTIONS, {
    fetchPolicy: "cache-first",
    skip: !flagsLoaded || !canManageAchievements,
    variables: { input: { search: null } }
  });
  const { Menu, setMenu } = useTableRowMenu();
  const [modalState, setModalState] = useState<AwardModalState | null>(null);
  const [achievementToDelete, setAchievementToDelete] = useState<MembershipAchievementForMember | null>(null);

  const tableData = useMemo(
    () => (data?.achievementsForMember?.length ? formatTableData(data.achievementsForMember) : []),
    [data?.achievementsForMember]
  );

  const columns = useMemo(
    () => [
      {
        header: "Title",
        id: "title",
        accessorKey: "title",
        size: 280,
        enableSorting: false,
        meta: {
          className: getStickyColumnClasses(!!tableData.length)
        }
      },
      {
        header: "Recognition Year",
        id: "recognitionYear",
        accessorKey: "recognitionYear",
        enableSorting: false
      },
      {
        header: "Recognized By",
        id: "recognizedBy",
        accessorKey: "recognizedBy",
        enableSorting: false
      },
      {
        header: "Recognized On",
        id: "recognizedOn",
        accessorKey: "recognizedOn",
        enableSorting: false
      },
      ...(canManageAchievements
        ? [
            buildActionColumn<AwardTableRow>({
              actionId,
              setMenu,
              getActions: (row): Action[] => {
                if (!row.award.canEdit) return [];

                return [
                  {
                    text: "Edit Award",
                    section: "Manage",
                    onClick: () => setModalState({ mode: "edit", award: row.award })
                  },
                  {
                    text: "Delete Achievement",
                    section: "Manage",
                    iconName: "delete",
                    destructive: true,
                    onClick: () => setAchievementToDelete(row.award)
                  }
                ];
              }
            })
          ]
        : [])
    ],
    [canManageAchievements, setMenu, tableData.length]
  );

  if (!flagsLoaded) return null;

  if (error) return <div>Error: {error.message}</div>;

  return (
    <>
      <div className="pt-12" />
      <TableHeading
        topSpacing="none"
        actions={
          canManageAchievements
            ? [
                {
                  label: "Add Award",
                  icon: "add",
                  variant: "filled",
                  onClick: () => setModalState({ mode: "add" }),
                  testId: "add-award-button"
                }
              ]
            : []
        }
      />
      <Table
        loading={loading}
        data={tableData}
        columns={columns}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Awards" caption="There are no awards for this person." iconName="emoji_events" />
        )}
        data-testid="career-awards-table"
      />
      <Menu />
      <EditAchievementModal
        open={modalState?.mode === "add" || modalState?.mode === "edit"}
        mode={modalState?.mode === "edit" ? "edit" : "add"}
        achievement={modalState?.mode === "edit" ? modalState.award : null}
        category="AWARD"
        pgaId={pgaId!}
        onClose={() => setModalState(null)}
      />
      <DeleteAchievementModal
        open={!!achievementToDelete}
        achievement={achievementToDelete}
        category="AWARD"
        pgaId={pgaId!}
        onClose={() => setAchievementToDelete(null)}
      />
    </>
  );
}
